import type {
	IBundleShoppingProduct,
	IProduct,
	IProductChild,
	IShoppingProduct
} from '@inter-ikea-kompis/types';

/**
 * Contains utility methods for SPRs used for sorting in ProductList.
 */
export default class ProductListSprUtility {
	/**
	 * Recursively flattens the provided products. If a product has children those are used. Otherwise the product itself is used.
	 *
	 * @param shoppingProducts A list of shopping products.
	 * @param includedProducts A list of products containing full product info for child products.
	 * @returns A recursively flattened list of all products and child products.
	 */
	public static flattenSprsOrBundles(
		shoppingProducts: (IShoppingProduct | IBundleShoppingProduct)[],
		includedProducts: IProduct[]
	): IShoppingProduct[];
	public static flattenSprsOrBundles(
		shoppingProducts: IShoppingProduct[],
		includedProducts?: IProduct[] | null
	): IShoppingProduct[];
	public static flattenSprsOrBundles(
		shoppingProducts: (IShoppingProduct | IBundleShoppingProduct)[],
		includedProducts?: IProduct[] | null
	): IShoppingProduct[] {
		if (!includedProducts) {
			return shoppingProducts as IShoppingProduct[];
		}

		const children = shoppingProducts
			.flatMap((sp) => Array(sp.quantity).fill(sp))
			.flatMap((sp) => sp.product.content?.child)
			.filter((child): child is IProductChild => !!child);

		const childrenWithoutDuplicates = this.mergeDuplicateChildren(children);

		let childProducts = childrenWithoutDuplicates
			.map((child: IProductChild): IShoppingProduct => {
				const itemNo = child.itemId.split('-').reverse()[0];
				const product = <IProduct>(
					includedProducts.find(
						(p) => itemNo === p.content?.itemNoLocal || itemNo === p.content?.itemNoGlobal
					)
				);
				return { product, quantity: child.quantity };
			})
			.filter(({ product }) => product);

		if (childProducts.length > 0) {
			childProducts = this.flattenSprsOrBundles(childProducts, includedProducts);
		}

		return [
			...(shoppingProducts.filter((sp) => !sp.product.content?.child) as IShoppingProduct[]),
			...childProducts
		];
	}

	/**
	 * Merges duplicated children and sums their quantities.
	 *
	 * @param children Children, with potentially duplicated products.
	 */
	private static mergeDuplicateChildren(children: IProductChild[]): IProductChild[] {
		const childMap = children.reduce((all, curr) => {
			const child = all[curr.itemId];
			if (child) {
				const quantity = child.quantity + curr.quantity;
				return { ...all, [curr.itemId]: { ...child, quantity } };
			}
			return { ...all, [curr.itemId]: curr };
		}, {});

		return Object.values(childMap);
	}
}
